/**
 * Per-campus payment breakdown for the final payment step.
 *
 * /calculatefees prices the combined family first (sibling / HCC / staff
 * discount uses every enrolled child, not each campus on its own). Each campus
 * row is that campus's children after that family discount — the same figures
 * the gateway charges.
 *
 * Prefer campusBreakdown.payableAmount from the server. Student rows are only
 * used for child counts and as a fallback when the server has not sent a
 * campus payable yet.
 *
 * `amountToPay` is the optional single-campus "Enter Fee in AUD" amount. It is
 * not used to re-split a multi-campus family; that split already lives on the
 * server campus rows.
 */
import { getChildCampusName, isFeeEligibleChild, toNumberAmount } from './parentHistoryHelpers';

const UNKNOWN_CAMPUS = 'Unknown campus';

const num = (value) => toNumberAmount(value) ?? 0;
const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const hasAmount = (value) => value !== undefined && value !== null && value !== '';

/** School fees after this child's family discount, plus resource fees. */
const studentAmountDue = (student) => {
    const resource = num(student.resource_fee);
    if (student.resource_fees_only) return resource;

    const school = num(student.school_fees_wo_any_disc);
    const discount =
        num(student.sibling_discount) +
        num(student.HCCDiscount) +
        num(student.discount);
    return Math.max(0, school - discount + resource);
};

const studentDiscount = (student) =>
    num(student.sibling_discount) +
    num(student.HCCDiscount) +
    num(student.discount) +
    num(student.resource_only_discount);

/**
 * Amount due for one campus as calculated by /calculatefees.
 * payableAmount already includes the family-wide discount for that campus.
 */
const serverCampusAmountDue = (campus) => {
    if (hasAmount(campus.payableAmount)) return num(campus.payableAmount);
    if (hasAmount(campus.PayableAmount)) return num(campus.PayableAmount);
    if (hasAmount(campus.amountToCover)) return num(campus.amountToCover);
    return null;
};

const emptyRow = (college) => ({
    college,
    SessionID: '',
    childCount: 0,
    schoolFees: 0,
    discount: 0,
    amountDue: 0,
    hasServerAmount: false,
    creditApplied: 0,
    creditAvailable: 0,
    cardAmount: 0,
    alreadyPaidAmount: 0,
    paymentStatus: null,
    paymentMode: null,
    DiscountOption: '',
    minimumAmount: 0,
    fullAmount: 0,
    TotalDepFees: 0,
});

/** Apply a typed custom amount to the single campus still owing. */
const allocateAmountToPay = (campuses, amountToPay) => {
    const target = round2(amountToPay);
    if (!Number.isFinite(target) || target < 0) return campuses;

    const openRows = campuses.filter((row) => !row.isPaid);
    if (!openRows.length) return campuses;

    const weightTotal = openRows.reduce((total, row) => total + row.amountDue, 0);
    let allocated = 0;

    openRows.forEach((row, index) => {
        const share = index === openRows.length - 1
            ? round2(target - allocated)
            : round2(weightTotal > 0
                ? (target * row.amountDue) / weightTotal
                : target / openRows.length);
        row.amountDue = share;
        allocated = round2(allocated + share);
    });

    campuses
        .filter((row) => row.isPaid && row.alreadyPaidAmount > 0)
        .forEach((row) => { row.amountDue = row.alreadyPaidAmount; });

    return campuses;
};

export const buildCampusFeeBreakdown = ({
    students = [],
    children = [],
    campusBreakdown = [],
    fallbackCampus = '',
    amountToPay,
} = {}) => {
    const rowsByCampus = new Map();

    const rowFor = (name) => {
        const college =
            String(name || '').trim() || String(fallbackCampus || '').trim() || UNKNOWN_CAMPUS;
        const key = college.toLowerCase();
        if (!rowsByCampus.has(key)) {
            rowsByCampus.set(key, emptyRow(college));
        }
        return rowsByCampus.get(key);
    };

    (students || []).forEach((student) => {
        const row = rowFor(getChildCampusName(student));
        row.childCount += 1;
        row.schoolFees += num(student.school_fees_wo_any_disc);
        row.discount += studentDiscount(student);
        row.amountDue += studentAmountDue(student);
    });

    // The gateway screen has no per-student fee rows, so the children supply the counts.
    if (!students?.length) {
        (children || []).filter(isFeeEligibleChild).forEach((child) => {
            rowFor(getChildCampusName(child)).childCount += 1;
        });
    }

    (campusBreakdown || []).forEach((campus) => {
        const row = rowFor(campus.college || getChildCampusName(campus));
        row.SessionID = campus.SessionID || row.SessionID;
        row.paymentStatus = campus.paymentStatus || row.paymentStatus;
        row.paymentMode = campus.paymentMode || row.paymentMode;
        row.DiscountOption = campus.DiscountOption || campus.selectedDiscount || row.DiscountOption;
        row.minimumAmount = hasAmount(campus.minimumAmount) ? num(campus.minimumAmount) : row.minimumAmount;
        row.fullAmount = hasAmount(campus.fullAmount) ? num(campus.fullAmount) : row.fullAmount;
        if (hasAmount(campus.TotalDepFees)) row.TotalDepFees = num(campus.TotalDepFees);
        row.creditApplied = num(campus.credit_applied ?? campus.AmountPaidFromAICCredit);
        row.creditAvailable = num(campus.credit_available);
        row.cardAmount = num(campus.AmountPaidFromBpoint);
        row.alreadyPaidAmount = num(campus.alreadyPaidAmount);

        const serverDue = serverCampusAmountDue(campus);
        if (serverDue != null) {
            row.amountDue = serverDue;
            row.hasServerAmount = true;
        } else if (!row.amountDue) {
            row.amountDue =
                num(campus.CustomAmount) ||
                row.cardAmount + row.creditApplied ||
                num(campus.TotalDepFees) + num(campus.TotalResAmount) ||
                row.alreadyPaidAmount;
        }

        if (hasAmount(campus.TotalDiscount)) {
            row.discount = num(campus.TotalDiscount);
        }
    });

    const campuses = [...rowsByCampus.values()].map((row) => ({
        ...row,
        schoolFees: round2(row.schoolFees),
        discount: round2(row.discount),
        amountDue: round2(row.amountDue),
        creditApplied: round2(row.creditApplied),
        creditAvailable: round2(row.creditAvailable),
        cardAmount: round2(row.cardAmount),
        alreadyPaidAmount: round2(row.alreadyPaidAmount),
        minimumAmount: round2(row.minimumAmount || row.TotalDepFees),
        fullAmount: round2(row.fullAmount || row.amountDue),
        TotalDepFees: round2(row.TotalDepFees),
        isPaid: row.paymentStatus === 'paid',
    }));

    // A typed custom amount is only for a single-campus visit. Multi-campus
    // amounts already come from /calculatefees and must not be re-split here.
    const hasServerAmounts = campuses.some((row) => row.hasServerAmount);
    const unpaidCount = campuses.filter((row) => !row.isPaid).length;
    const shouldAllocateCustom =
        amountToPay !== undefined &&
        amountToPay !== null &&
        amountToPay !== '' &&
        (!hasServerAmounts || unpaidCount <= 1);

    const paymentRows = shouldAllocateCustom
        ? allocateAmountToPay(campuses, Number(amountToPay))
        : campuses;

    const displayRows = paymentRows.map(({ hasServerAmount, ...row }) => row);
    const sum = (key) => round2(displayRows.reduce((total, row) => total + row[key], 0));

    return {
        campuses: displayRows,
        totals: {
            childCount: campuses.reduce((total, row) => total + row.childCount, 0),
            schoolFees: sum('schoolFees'),
            discount: sum('discount'),
            amountDue: sum('amountDue'),
            creditApplied: sum('creditApplied'),
            cardAmount: sum('cardAmount'),
            alreadyPaidAmount: sum('alreadyPaidAmount'),
        },
    };
};

export default buildCampusFeeBreakdown;
